import {
  Component,
  computed,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { AppConfigService } from '@core/services/config/app-config.service';
import { DatabaseDisplayNamePipe } from '@shared/pipes/database-display-name/database-display-name.pipe';
import { Store } from '@ngrx/store';
import {
  environmentActions,
  selectEnvironmentInfo,
  selectEnvironmentType,
  selectServiceVersionsLoading,
  selectWebVersion,
  selectApiServiceVersion,
  selectPmServiceVersion,
  selectQuartzServiceVersion,
  selectS3NotifierVersion,
} from '@core/state/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  EnvironmentWarningDialogComponent,
} from '@core/components/environment-warning-dialog/environment-warning-dialog.component';
import { SignalRStore } from '@core/services/signal-r/signal-r.service';
import { SyrfDialogCloseDirective } from '@shared/dialog/syrf-dialog-close.directive';
import {
  SyrfDialogActionsDirective,
  SyrfDialogLayoutComponent,
} from '@shared/dialog/syrf-dialog-layout.component';
import { SyrfDialogService } from '@shared/dialog/syrf-dialog.service';
import { MatExpButton, MatExpIconButton } from '@camaradesuk/m3e-angular';

/** The heading openers pass as the dialog title. */
export const VERSION_INFO_DIALOG_TITLE = 'Version Information';

@Component({
  selector: 'app-version-info-dialog',
  templateUrl: './version-info-dialog.component.html',
  styleUrls: ['./version-info-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    SyrfDialogLayoutComponent,
    SyrfDialogActionsDirective,
    SyrfDialogCloseDirective,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    FlexLayoutModule,
    DatabaseDisplayNamePipe,
    MatExpButton,
    MatExpIconButton,
  ],
})
export class VersionInfoDialogComponent implements OnInit {
  private readonly configService = inject(AppConfigService);
  private readonly store = inject(Store);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(SyrfDialogService);
  private readonly signalRStore = inject(SignalRStore);

  readonly config = this.configService.config;
  readonly envInfo = this.store.selectSignal(selectEnvironmentInfo);
  private readonly _environmentType = this.store.selectSignal(
    selectEnvironmentType
  );

  // Service versions from store (with "CHANGEME" transformation applied by selectors)
  readonly serviceVersionsLoading = this.store.selectSignal(
    selectServiceVersionsLoading
  );
  readonly webVersion = this.store.selectSignal(selectWebVersion);
  readonly apiVersion = this.store.selectSignal(selectApiServiceVersion);
  readonly pmVersion = this.store.selectSignal(selectPmServiceVersion);
  readonly quartzVersion = this.store.selectSignal(selectQuartzServiceVersion);
  readonly s3NotifierVersion = this.store.selectSignal(selectS3NotifierVersion);

  // Additional info (from config, not store)
  readonly buildInfo = {
    gitVersion: this.config.gitVersion,
    gitInformationalVersion: this.config.gitInformationalVersion,
    gitSha: this.config.gitSha,
    environment: this.config.runtimeEnvironment,
  };

  // Live updates (SignalR) status
  readonly liveUpdatesEnabled = computed(() => this.config.signalRActive);
  readonly liveUpdatesConnected = toSignal(
    this.signalRStore.select((state) => state.connected),
    { initialValue: false }
  );
  readonly liveUpdatesStatus = computed(() => {
    if (!this.liveUpdatesEnabled()) return 'not-configured';
    return this.liveUpdatesConnected() ? 'healthy' : 'unavailable';
  });
  readonly liveUpdatesLabel = computed(() => {
    if (!this.liveUpdatesEnabled()) return 'Disabled in configuration';
    return this.liveUpdatesConnected() ? 'Connected' : 'Disconnected';
  });
  readonly liveUpdatesTooltip = computed(() => {
    if (!this.liveUpdatesEnabled())
      return 'Real-time updates are disabled (signalRActive=false)';
    return this.liveUpdatesConnected()
      ? 'Real-time updates are active'
      : 'Not connected: changes may not appear in real time';
  });

  /**
   * Returns true if the database is not the production database.
   * Uses server-derived databaseType for unambiguous identification.
   */
  readonly isNonProductionDatabase = computed(() => {
    const dbType = this.envInfo()?.databaseType;
    if (!dbType) return false;
    return dbType !== 'production';
  });

  /**
   * Returns true if connected to the production database.
   * Uses server-derived databaseType for unambiguous identification.
   */
  readonly isProductionDatabase = computed(() => {
    return this.envInfo()?.databaseType === 'production';
  });

  ngOnInit(): void {
    // Dispatch action to load service versions when dialog opens
    this.store.dispatch(environmentActions.loadServiceVersions());
  }

  refresh(): void {
    // Dispatch action to reload service versions
    this.store.dispatch(environmentActions.loadServiceVersions());
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'healthy':
        return 'check_circle';
      case 'degraded':
        return 'warning';
      case 'unhealthy':
        return 'error';
      case 'unavailable':
        return 'help_outline';
      case 'not-configured':
        return 'block';
      default:
        return 'hourglass_empty';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'healthy':
        return '#4caf50';
      case 'degraded':
        return '#ff9800';
      case 'unhealthy':
        return '#f44336';
      case 'unavailable':
        return '#9e9e9e';
      case 'not-configured':
        return '#9e9e9e';
      default:
        return '#ff9800';
    }
  }

  /**
   * Opens the Environment Warning Dialog to show details about non-production database.
   */
  openEnvironmentWarning(): void {
    EnvironmentWarningDialogComponent.open(this.dialog, this._environmentType(), {
      width: '500px',
      autoFocus: false,
    });
  }

  async copyToClipboard(): Promise<void> {
    const envInfo = this.envInfo();
    const webVer = this.webVersion();
    const apiVer = this.apiVersion();
    const pmVer = this.pmVersion();
    const quartzVer = this.quartzVersion();
    const s3Ver = this.s3NotifierVersion();

    const info = {
      web: {
        version: webVer?.version ?? this.config.displayVersion,
        gitVersion: this.config.gitVersion,
        gitSha: this.config.gitSha,
        environment: this.config.runtimeEnvironment,
      },
      api: {
        version: apiVer?.version ?? null,
        status: apiVer?.status ?? 'loading',
        sha: apiVer?.sha,
      },
      pm: {
        version: pmVer?.version ?? null,
        status: pmVer?.status ?? 'loading',
        sha: pmVer?.sha,
      },
      quartz: {
        version: quartzVer?.version ?? null,
        status: quartzVer?.status ?? 'loading',
        sha: quartzVer?.sha,
      },
      s3Notifier: {
        version: s3Ver?.version ?? null,
        status: s3Ver?.status ?? 'loading',
      },
      environment: envInfo
        ? {
            name: envInfo.environment,
            database: envInfo.databaseName,
            databaseLocation: envInfo.databaseLocation,
            databaseType: envInfo.databaseType,
            prNumber: envInfo.prNumber,
            isProduction: envInfo.isProduction,
            apiVersion: envInfo.apiVersion,
            monorepoSha: envInfo.monorepoSha,
          }
        : null,
      liveUpdates: {
        enabled: this.liveUpdatesEnabled(),
        connected: this.liveUpdatesConnected(),
        status: this.liveUpdatesStatus(),
      },
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(info, null, 2));
      this.snackBar.open('Version info copied to clipboard', 'Dismiss', {
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      this.snackBar.open('Failed to copy to clipboard', 'Dismiss', {
        duration: 3000,
      });
    }
  }
}
